import { type HotelType, type FacilityType } from "../../api/HotelListResponse";
import type { Checkable } from "./HotelFilter/FilterComponents/CheckboxList";

export type HotelsFilterState = {
  priceLowerBound: number | undefined;
  priceUpperBound: number | undefined;
  selectPriceLower: number | undefined;
  selectPriceUpper: number | undefined;
  popular: Checkable;
  paymentOptions: Checkable;
  guestRating: Checkable;
  starRating: StarRatingType;
  amenities: FacilityType[];
  propertyTypes: HotelType[];
  userCurrency: String;
};

export type HotelsFilterAction =
  | { type: "CLEAR_ALL" }
  | { type: "CLEAR_PRICE" }
  | { type: "UPDATE_FILTERS"; payload: HotelsFilterState }
  | { type: "UPDATE_POPULAR"; popular: Checkable }
  | { type: "UPDATE_PAYMENT_OPTIONS"; paymentOptions: Checkable }
  | { type: "UPDATE_GUEST_RATING"; guestRating: Checkable }
  | { type: "UPDATE_STAR_RATING"; starRating: StarRatingType }
  | { type: "UPDATE_AMENITIES"; amenities: FacilityType[] }
  | { type: "UPDATE_PROPERTY_TYPE"; propertyTypes: HotelType[] }
  | { type: "UPDATE_PRICE_SELECTION"; priceSelection: PriceSelection }
  | { type: "POPULATE_PRICE_RANGE"; priceRange: PriceRange; currency: String }
  | { type: "POPULATE_GUEST_RATING"; maxReviewScore: number };

export type StarRatingType = {
  "1": boolean;
  "2": boolean;
  "3": boolean;
  "4": boolean;
  "5": boolean;
};

// Some options are universal, and can be hard-coded.
export const defaultFilterState: HotelsFilterState = {
  priceLowerBound: undefined,
  priceUpperBound: undefined,
  selectPriceLower: undefined,
  selectPriceUpper: undefined,
  popular: {},
  paymentOptions: {},
  guestRating: {},
  starRating: {
    "1": false,
    "2": false,
    "3": false,
    "4": false,
    "5": false,
  },
  amenities: [],
  propertyTypes: [],
  userCurrency: "",
};

export const filtersReducer = (
  state: HotelsFilterState,
  action: HotelsFilterAction,
): HotelsFilterState => {
  switch (action.type) {
    case "CLEAR_ALL":
      return {
        ...state,
        selectPriceLower: state.priceLowerBound,
        selectPriceUpper: state.priceUpperBound,
        paymentOptions: falsifyFlagObject(state.paymentOptions),
        guestRating: falsifyFlagObject(state.guestRating),
        starRating: defaultFilterState.starRating,
        amenities: [],
        propertyTypes: [],
      };
    case "CLEAR_PRICE":
      return {
        ...state,
        selectPriceLower: state.priceLowerBound,
        selectPriceUpper: state.priceUpperBound,
      };
    case "UPDATE_FILTERS":
      return {
        ...state,
        ...action.payload,
      };

    case "UPDATE_POPULAR":
      return {
        ...state,
        popular: action.popular,
      };
    case "UPDATE_PAYMENT_OPTIONS":
      return {
        ...state,
        paymentOptions: action.paymentOptions,
      };
    case "UPDATE_GUEST_RATING":
      return {
        ...state,
        guestRating: action.guestRating,
      };
    case "UPDATE_STAR_RATING":
      return {
        ...state,
        starRating: action.starRating,
      };
    case "UPDATE_AMENITIES":
      return {
        ...state,
        amenities: action.amenities,
      };
    case "UPDATE_PROPERTY_TYPE":
      return {
        ...state,
        propertyTypes: action.propertyTypes,
      };

    case "UPDATE_PRICE_SELECTION":
      return {
        ...state,
        selectPriceLower: action.priceSelection.lower,
        selectPriceUpper: action.priceSelection.upper,
      };

    case "POPULATE_PRICE_RANGE":
      const minPrice = action.priceRange.min;
      const maxPrice = action.priceRange.max;
      const currency = action.currency;
      let selectPriceUpper;
      let selectPriceLower;

      // Preserve existing selection unless out of price range. Reset on currency change
      if (currency !== state.userCurrency) {
        selectPriceUpper = maxPrice;
        selectPriceLower = minPrice;
      } else {
        selectPriceUpper = Math.min(
          state?.selectPriceUpper ?? Number.POSITIVE_INFINITY,
          maxPrice,
        );
        selectPriceLower = Math.max(state?.selectPriceLower ?? 0, minPrice);
      }

      return {
        ...state,
        priceLowerBound: minPrice,
        priceUpperBound: maxPrice,
        selectPriceLower: selectPriceLower,
        selectPriceUpper: selectPriceUpper,
        userCurrency: currency,
      };

    case "POPULATE_GUEST_RATING":
      const maxReviewScore = action.maxReviewScore;
      let guestRating;
      // Preserve existing selections
      if (
        Object.keys(state.guestRating).length > 0 &&
        state.guestRating.constructor === Object
      ) {
        guestRating = state.guestRating;
      } else if (maxReviewScore === 5) {
        guestRating = {
          "0": false,
          "1": false,
          "2": false,
          "3": false,
          "4": false,
        };
      } else if (maxReviewScore === 10) {
        guestRating = {
          "0": false,
          "6": false,
          "7": false,
          "8": false,
          "9": false,
        };
      } else {
        guestRating = {};
      }
      return {
        ...state,
        guestRating: guestRating,
      };

    default:
      throw new Error(`Unknown action type`);
  }
};

export const countActivatedFilters = (state: HotelsFilterState) => {
  let count = 0;
  Object.keys(state).forEach((filter) => {
    if (filter === "amenities") {
      count += state.amenities.length;
    }
    if (filter === "propertyTypes") {
      count += state.propertyTypes.length;
    }
    if (
      filter === "provider" ||
      filter === "maxReviewScore" ||
      filter === "propertyTypes" ||
      filter === "priceLow" ||
      filter === "priceHigh" ||
      filter === "selectPriceLower" ||
      filter === "selectPriceUpper" ||
      filter === "userCurrency" ||
      filter === "amenities"
    ) {
      // Done counting for this key in filterState.
      return;
    }

    const filterObject = state[filter as keyof HotelsFilterState];
    if (filterObject === undefined) return;
    count += Object.values(filterObject).reduce((acc, curr) => acc + curr, 0);
  });

  return count;
};

export function isHotelsOnly(propertyTypes: HotelType[]) {
  return propertyTypes.length === 1 && propertyTypes[0] === "Hotel";
}

const falsifyFlagObject = (object: { [key: string]: boolean }) => {
  const shallowCopy = { ...object };
  Object.keys(shallowCopy).forEach((option) => (shallowCopy[option] = false));
  return shallowCopy;
};

type PriceRange = {
  min: number;
  max: number;
};
type PriceSelection = {
  lower: number;
  upper: number;
};
